// ─────────────────────────────────────────────────────────────────────────
// `/checkout/<sku>` — the page that takes the money.
//
// THE ADDRESS IS THE PRODUCT'S. One link per product, which is what an owner
// copies out of Packaging and puts behind the Buy button on whatever page they
// built. It lives on the MARKETING host beside `/p/<slug>` for the same
// reason: the person reading it has never signed in.
//
//   /checkout/ai-website-studio                            pay
//   /checkout/ai-website-studio/thanks?payment_intent=pi_…  paid
//   ?view=checkout&sku=<sku>&state=thanks                   local dev
//
// Dispatched by `MarketingV2App` exactly as `/p/` and `/score` are. No router
// dependency, matching every other surface in this repo.
// ─────────────────────────────────────────────────────────────────────────

export type CheckoutViewState = 'pay' | 'thanks';

export interface CheckoutRoute {
  /** Empty when the address names nothing valid — the surface renders not-found. */
  sku: string;
  state: CheckoutViewState;
  /** Only ever set on the thank-you page. */
  paymentIntentId: string | null;
  /**
   * Stripe's own secret for that payment, when it is on the address.
   *
   * THIS IS WHAT MAKES THE THANK-YOU PAGE ABLE TO TELL THE TRUTH. A payment id
   * on its own is a string anybody could type; the secret is what lets the page
   * ask Stripe what actually happened to the payment instead of believing the
   * address. Without it the page says it cannot confirm, which is the honest
   * thing rather than a congratulation nobody earned.
   */
  clientSecret: string | null;
}

const PREFIX = '/checkout/';
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
/** Same shape the server validates. Checked here too — it goes to a public endpoint. */
const PAYMENT_ID_PATTERN = /^pi_[A-Za-z0-9_]{4,}$/;
/** `pi_…_secret_…`. Stripe's secret always begins with the payment it opens. */
const CLIENT_SECRET_PATTERN = /^pi_[A-Za-z0-9_]{4,}_secret_[A-Za-z0-9_-]{4,}$/;

/** Cheap and total: called before the marketing surface renders anything. */
export function isCheckoutPath(pathname: string, search = ''): boolean {
  if (pathname.startsWith(PREFIX)) return true;
  return new URLSearchParams(search).get('view') === 'checkout';
}

export function parseCheckoutRoute(pathname: string, search = ''): CheckoutRoute {
  const params = new URLSearchParams(search);

  let raw = '';
  let state: CheckoutViewState;

  if (pathname.startsWith(PREFIX)) {
    // WHICHEVER NAMED THE PRODUCT NAMES THE PAGE. On a real address the path
    // is the only thing that may say a payment happened — a link shared with a
    // stray `?state=thanks` on the end must not land a buyer on the thank-you
    // view with nothing behind it.
    const rest = decodeURIComponent(pathname.slice(PREFIX.length)).replace(/\/+$/, '');
    const [first, second] = rest.split('/');
    raw = first ?? '';
    state = second === 'thanks' ? 'thanks' : 'pay';
  } else {
    // The dev fallback has no path to read, so there the parameter is the
    // mechanism rather than a claim about a payment.
    raw = params.get('sku') ?? '';
    state = params.get('state') === 'thanks' ? 'thanks' : 'pay';
  }

  const cleaned = raw.trim().toLowerCase();
  const rawPaymentId = params.get('payment_intent')?.trim() ?? '';
  const paymentIntentId = PAYMENT_ID_PATTERN.test(rawPaymentId) ? rawPaymentId : null;

  // A SECRET ONLY COUNTS BESIDE THE PAYMENT IT OPENS. Stripe's own secret is
  // the payment's id with `_secret_…` on the end, so a mismatched pair is one
  // nobody honest put together — and a secret with no payment named beside it
  // has nothing for the page to be about.
  const rawSecret = params.get('payment_intent_client_secret')?.trim() ?? '';
  const secretMatches =
    paymentIntentId !== null &&
    CLIENT_SECRET_PATTERN.test(rawSecret) &&
    rawSecret.startsWith(`${paymentIntentId}_secret_`);

  return {
    sku: SLUG_PATTERN.test(cleaned) ? cleaned : '',
    state,
    paymentIntentId,
    clientSecret: secretMatches ? rawSecret : null,
  };
}

/**
 * Absolute URL for a checkout state — what Stripe is handed as the address to
 * come back to after a payment that needed the buyer's bank.
 *
 * THE SAME TWO PARAMETERS STRIPE WRITES ITSELF. A payment that went off to a
 * bank comes back carrying `payment_intent` and `payment_intent_client_secret`;
 * a payment confirmed in the page never leaves, so the page writes the identical
 * pair on its own way to the thank-you address. One pair of parameters, one code
 * path on the other side, and the same check against Stripe either way.
 */
export function checkoutUrl(
  sku: string,
  state: CheckoutViewState,
  origin: string,
  paymentIntentId?: string | null,
  clientSecret?: string | null,
): string {
  const base = `${origin.replace(/\/$/, '')}${PREFIX}${encodeURIComponent(sku)}${
    state === 'thanks' ? '/thanks' : ''
  }`;
  if (!paymentIntentId) return base;

  const params = new URLSearchParams({ payment_intent: paymentIntentId });
  if (clientSecret) params.set('payment_intent_client_secret', clientSecret);
  return `${base}?${params.toString()}`;
}
